<script setup lang="ts">
import type { ShortsSectionVariant } from './variants';
import type { ShortVideo } from '~/composables/shorts';
import type { ShortsFilters } from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';

import { cn, useStaleGuard } from '@/utils';
import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useFetchShorts, useLastVideoState } from '~/composables/shorts';
import {
	hasRecommendationReference,
	shortsFiltersToListingQuery
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isEqual } from 'ohash';

import { blessSharedVideoElement } from '~/components/PropertyReelsFeed/useIOSSharedVideo';
import { VideoCard } from '~/components/VideoCard';

import SectionSkeleton from './SectionSkeleton.vue';
import { getSectionStyles } from './variants';

const MAX_VIDEOS = 15;

const props = withDefaults(
	defineProps<{
		title: string;
		filters?: ShortsFilters;
		variant?: ShortsSectionVariant;
		showTitleIcon?: boolean;
		singleCardOnLg?: boolean;
		disablePeekFromLg?: boolean;
		useCompactMobileTopSpacing?: boolean;
		useParentMobileGutter?: boolean;
		hideBottomBorder?: boolean;
		enableRecommendationFallback?: boolean;
		preserveFeedEntryContext?: boolean;
		sliderSectionName?: SectionName;
		videoCardSectionName?: SectionName;
		ctaSectionName?: SectionName;
		videoCardPositionMode?: 'absolute' | 'visible';
	}>(),
	{
		variant: 'dark',
		showTitleIcon: true,
		singleCardOnLg: false,
		disablePeekFromLg: false,
		useCompactMobileTopSpacing: false,
		useParentMobileGutter: false,
		hideBottomBorder: false,
		enableRecommendationFallback: false,
		preserveFeedEntryContext: false,
		sliderSectionName: 'shorts',
		videoCardSectionName: 'shorts hero section',
		ctaSectionName: 'shorts',
		videoCardPositionMode: 'absolute'
	}
);

const styles = computed(() => getSectionStyles(props.variant));
const sectionClass = computed(() =>
	cn(styles.value.section, props.hideBottomBorder && 'border-b-0')
);
const containerClass = computed(() =>
	cn(styles.value.containerClass, props.useCompactMobileTopSpacing && 'pt-4')
);
const carouselItemClass = computed(() =>
	cn(
		styles.value.carouselItemClass,
		props.disablePeekFromLg &&
			'lg:basis-[calc(50%-9px)] xl:basis-[calc(33.33333%-12px)]',
		props.singleCardOnLg && 'lg:basis-full'
	)
);
const usesParentMobileGutter = computed(
	() => props.variant === 'light' && props.useParentMobileGutter
);
const headerClass = computed(() =>
	cn(styles.value.headerClass, usesParentMobileGutter.value && 'px-0')
);
const footerClass = computed(() =>
	cn(styles.value.footerClass, usesParentMobileGutter.value && 'px-0')
);

const carouselOptions = computed(() => ({
	cyclic: true,
	class: styles.value.contentGap,
	carouselWrapperClasses: cn(
		styles.value.carouselWrapperClass,
		usesParentMobileGutter.value && 'px-0'
	)
}));

const emit = defineEmits<{
	(e: 'update:has-videos', value: boolean): void;
	(e: 'update:used-fallback', value: boolean): void;
}>();

const { fetchVideos } = useFetchShorts();

const allVideos = ref<ShortVideo[]>([]);
const isLoading = ref(false);
const isPreloaded = ref(false);

const videos = computed(() =>
	allVideos.value
		.filter(
			(item): item is ShortVideo & { videoUrl: string } => item.videoUrl != null
		)
		.slice(0, MAX_VIDEOS)
);

const isMobileOrTablet = ref(false);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);

const sliderRef = ref<{
	currentCarouselItemIndex: number;
	scrollTo: (index: number) => void;
} | null>(null);

const shouldAutoplay = computed(
	() => isMobileOrTablet.value && isSectionVisible.value
);

const hasVideos = computed(() => videos.value.length > 0);

const shouldShowSection = computed(
	() => !isPreloaded.value || isLoading.value || hasVideos.value
);

const fetchGuard = useStaleGuard();

async function loadVideos(filters: ShortsFilters): Promise<void> {
	const capturedId = fetchGuard.next();
	isLoading.value = true;
	try {
		const result = await fetchVideos(filters, 1, undefined, {
			enableRecommendationFallback: props.enableRecommendationFallback
		});
		if (fetchGuard.isStale(capturedId)) return;
		const appliedFilters = result.appliedFilters ?? filters;
		const usedFallback =
			props.enableRecommendationFallback &&
			hasRecommendationReference(filters) &&
			Boolean(
				appliedFilters.displayInHomeShorts && !appliedFilters.referenceProperty
			);
		allVideos.value = result.items;
		isPreloaded.value = true;
		emit('update:used-fallback', usedFallback);
	} finally {
		if (!fetchGuard.isStale(capturedId)) {
			isLoading.value = false;
		}
	}
}

onMounted(() => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobileOrTablet.value = breakpoints.smaller('lg').value;

	watch(breakpoints.smaller('lg'), val => {
		isMobileOrTablet.value = val;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) {
				isSectionVisible.value = entry.isIntersecting;
			}
		},
		{ threshold: 0.3 }
	);

	requestIdleCallback(() => {
		loadVideos(props.filters ?? {});
	});
});

watch(
	() => props.filters,
	(newFilters, oldFilters) => {
		if (isEqual(newFilters, oldFilters)) return;
		loadVideos(newFilters ?? {});
	},
	{ deep: true }
);

watch(
	hasVideos,
	value => {
		emit('update:has-videos', value);
	},
	{ immediate: true }
);

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const lastVideoState = useLastVideoState();

function handleVideoCardClick(index: number): void {
	const item = videos.value[index];
	if (!item) return;

	const { commercialId } = item.property;
	const path = `/shorts/${commercialId}`;

	lastVideoState.save(commercialId, index);
	blessSharedVideoElement();

	if (!props.preserveFeedEntryContext) {
		navigateTo(path);
		return;
	}

	navigateTo({
		path,
		query: shortsFiltersToListingQuery(props.filters ?? {})
	});
}

function handleCtaClick(): void {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Explorar todos os Shorts',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: props.ctaSectionName,
		[MixpanelProps.DESTINATION_URL]: '/shorts'
	});
	navigateTo('/shorts');
}

function getVideoCardPosition(index: number): number {
	if (props.videoCardPositionMode !== 'visible') {
		return index;
	}

	const currentCarouselItemIndex =
		sliderRef.value?.currentCarouselItemIndex ?? 0;

	return Math.max(1, index - currentCarouselItemIndex + 1);
}
</script>

<template>
	<SectionSkeleton
		v-if="shouldShowSection && !isPreloaded"
		:title="title"
		:variant="variant"
		:hide-bottom-border="props.hideBottomBorder"
	/>

	<section v-else-if="shouldShowSection" ref="sectionRef" :class="sectionClass">
		<StandardSlider
			ref="sliderRef"
			:items="videos"
			:is-loading="false"
			:carousel-item-class="carouselItemClass"
			:carousel-options="carouselOptions"
			:limit-photos="false"
			:section-name="props.sliderSectionName"
			:container-class="containerClass"
			:footer-class="footerClass"
			:carousel-content-class="styles.carouselContentClass"
			:selector-variant="styles.selectorVariant"
			:selector-arrows-variant="styles.selectorArrowsVariant"
			:show-navigation-arrows="false"
		>
			<template #header>
				<header :class="headerClass">
					<div class="flex w-full flex-col items-start justify-between">
						<div class="flex items-center gap-3">
							<IconMaterialSymbolsLightPlayCircle
								v-if="showTitleIcon"
								:class="cn('size-7 md:size-12', styles.titleText)"
							/>
							<h2 :class="cn(styles.titleFont, styles.titleText)">
								{{ title }}
							</h2>
						</div>
					</div>
				</header>
			</template>

			<template #items="{ item, index }">
				<VideoCard
					:property="item.property"
					:thumbnail-url="item.thumbnailUrl"
					:video-url="item.videoUrl"
					:index="index"
					:card-position="getVideoCardPosition(index)"
					:section-name="props.videoCardSectionName"
					:is-active="
						shouldAutoplay && sliderRef?.currentCarouselItemIndex === index
					"
					:hover-to-play="!isMobileOrTablet"
					@click="handleVideoCardClick"
				/>
			</template>

			<template #cta>
				<Button
					v-if="styles.showCtaButton"
					:variant="styles.buttonVariant"
					size="responsive"
					class="text-base font-medium"
					@click="handleCtaClick"
				>
					<span>Explorar todos os Shorts</span>
					<IconMaterialSymbolsLightNorthEast
						:class="cn('size-5 xl:size-6', styles.ctaArrowClass)"
					/>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
